import React, { useState, useEffect } from "react";
import {
	IonCard,
	IonCardContent,
	IonCardHeader,
	IonCardSubtitle,
	IonCardTitle,
	IonIcon,
	IonItem,
	IonToolbar,
	IonCol,
	IonGrid,
	IonRow,
	IonLabel,
	IonButton,
	useIonAlert,
	IonPage,
	IonHeader,
	IonButtons,
	IonContent,
	IonMenuButton,
	IonTitle,
	IonNote,
	IonInput,
	IonModal,
	IonText,
	IonSelect,
	IonSelectOption,
	IonList
} from "@ionic/react";
import {
	add,
	addCircleOutline,
	closeSharp,
	pencil,
	save,
	trash
} from "ionicons/icons";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import NoDataFound from "../components/NoDataFound";
import PlusIcon from "../components/PlusIcon";
import { Button, Date } from "../controls";
import Utils from "../utils/utils";
import useScreenSize, { BreakPoint } from "../hooks/useScreenSize";
import useStore from "../store/store";

const Expanses: React.FC = () => {
	const { t } = useTranslation();
	const [from, setFrom] = useState<string>(
		moment().startOf("month").format("YYYY-MM-DDTHH:mm:ssZ")
	);
	const [to, setTo] = useState<string>(
		moment().endOf("month").format("YYYY-MM-DDTHH:mm:ssZ")
	);
	const size = useScreenSize();
	const [data, setData] = useState<any[]>([]);
	const [propertiesList, setPropertiesList] = useState<any[]>([]);
	const [presentAlert] = useIonAlert();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [toEditId, setToEditId] = useState("");

	let [description, setDescription] = useState<string>("");
	let [expense, setExpense] = useState<string>("");
	let [date, setDate] = useState<string>(
		moment().format("YYYY-MM-DDTHH:mm:ssZ")
	);
	let [propertyId, setPropertyId] = useState<string>("");
	let [isDisabled, setIsDisabled] = useState<boolean>(false);
	const { token, setIsLoading } = useStore((state: any) => state);

	let [error, setError] = useState("");
	let [msg, setMsg] = useState("");

	useEffect(() => {
		getPropertiesData();
	}, []);

	useEffect(() => {
		getData();
	}, [from, to]);

	const getData = () => {
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/transactions/all`,
				{
					keyword: "",
					type: "income",
					from,
					to
				},
				{ headers: { rentapp_auth: token } }
			)
			.then(res => setData(res.data))
			.catch(err => console.log(err));
	};

	const handleDelete = (id: any) => {
		presentAlert({
			header: `${t("sureToDel")}`,
			buttons: [
				{
					text: `${t("cancel")}`,
					role: "cancel"
				},
				{
					text: `${t("yes")}`,
					role: "confirm",
					handler: () => {
						axios
							.delete(
								`${process.env.REACT_APP_API_URL}/transactions/${id}`,
								{ headers: { rentapp_auth: token } }
							)
							.then(res => {
								getData();
							})
							.catch(err => console.log(err));
					}
				}
			]
		});
	};

	const handleEdit = (id: any) => {
		let elem = data.find((elem: any) => elem._id === id);
		setIsModalOpen(true);
		setDescription(elem.description);
		setDate(moment(elem.date).format("YYYY-MM-DDTHH:mm:ssZ"));
		setExpense(elem.amount.toString());
		setPropertyId(elem.propertyId);
		setToEditId(elem._id);
		setError("");
	};

	const addRent = () => {
		if (expense === "" || description === "" || propertyId === "") {
			setError(`${t("fillForm")}`);
			return;
		}
		if (!/^[+-]?\d+(\.\d+)?$/.test(expense)) {
			setError(`${t("addAmountError")}`);
			return;
		}

		const tenantId =
			propertiesList.find(k => k._id === propertyId).tenantId || "";

		if (!tenantId) {
			setError(`${t("notRentedCantAddRent")}`);
			return;
		}

		setIsLoading(true);
		setIsDisabled(true);

		if (toEditId === "") {
			axios
				.post(
					`${process.env.REACT_APP_API_URL}/transactions`,
					{
						propertyId: propertyId,
						tenantId: tenantId,
						description,
						amount: expense,
						type: "income",
						date: date
					},
					{ headers: { rentapp_auth: token } }
				)
				.then(res => {
					setError("");
					clearForm();
					setIsModalOpen(false);
					getData();
					setIsLoading(false);
					setIsDisabled(false);
				})
				.catch(err => {
					setError(err.response.data);
					setIsLoading(false);
					setIsDisabled(false);
				});
		} else {
			axios
				.put(
					`${process.env.REACT_APP_API_URL}/transactions/u/${toEditId}`,
					{
						propertyId: propertyId,
						tenantId: tenantId,
						description,
						amount: expense,
						type: "income",
						date: date
					},
					{ headers: { rentapp_auth: token } }
				)
				.then(res => {
					setError("");
					setIsModalOpen(false);
					setToEditId("");
					getData();
					clearForm();
					setIsLoading(false);
					setIsDisabled(false);
				})
				.catch(err => {
					setError(err.response.data);
					setIsLoading(false);
					setIsDisabled(false);
				});
		}
	};

	const clearForm = () => {
		setDescription("");
		setExpense("");
		setDate(moment().format("YYYY-MM-DDTHH:mm:ssZ"));
		setPropertyId("");
		setToEditId("");
		setError("");
	};

	const getPropertiesData = () => {
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/properties/all`,
				{
					keyword: ""
				},
				{ headers: { rentapp_auth: token } }
			)
			.then(res => setPropertiesList(res.data))
			.catch(err => console.log(err));
	};

	return (
		<>
			<IonModal
				isOpen={isModalOpen}
				trigger="open-modal"
				backdropDismiss={false}
			>
				<IonHeader>
					<IonToolbar>
						{toEditId === "" ? (
							<IonTitle>{t("addRent")}</IonTitle>
						) : (
							<IonTitle>{t("editRent")}</IonTitle>
						)}
						<IonButtons slot="end">
							<IonButton onClick={() => setIsModalOpen(false)}>
								<IonIcon icon={closeSharp}></IonIcon>
							</IonButton>
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent className="ion-padding ion-text-center">
					<IonItem>
						<IonLabel position="floating">
							{t("addDescription")}
						</IonLabel>
						<IonInput
							placeholder={t("addDescription") || ""}
							value={description}
							onIonInput={(e: any) =>
								setDescription(e.target.value)
							}
							onKeyUp={() => {
								setError("");
								setMsg("");
							}}
						></IonInput>
					</IonItem>
					<br />
					<IonItem>
						<IonLabel position="floating">
							{t("selectProperty")}
						</IonLabel>
						<IonSelect
							placeholder={t("selectProperty") || ""}
							value={propertyId}
							onIonChange={(e: any) =>
								setPropertyId(e.target.value)
							}
							okText={t("ok") || "Yes"}
							cancelText={t("cancel") || "Cancel"}
						>
							{propertiesList.length === 0 && <NoDataFound />}
							{propertiesList.length > 0 &&
								propertiesList.map((elem, index) => {
									return (
										<IonSelectOption
											value={elem._id}
											key={index}
										>
											{elem.address}
										</IonSelectOption>
									);
								})}
						</IonSelect>
					</IonItem>
					<br />
					<IonItem>
						<IonLabel position="floating">{t("amount")}</IonLabel>
						<IonInput
							placeholder={t("currencySymbol") || ""}
							type="number"
							value={expense}
							onIonInput={(e: any) => setExpense(e.target.value)}
							onKeyUp={() => {
								setError("");
								setMsg("");
							}}
						></IonInput>
					</IonItem>
					<br />
					<Date
						id="date"
						value={date}
						onChange={(e: any) => setDate(e.target.value)}
					/>
					<br />

					<Button
						text={toEditId === "" ? t("addRent") : t("saveChanges")}
						onClick={addRent}
						icon={toEditId === "" ? add : save}
						disabled={isDisabled}
					/>

					<br />
					<IonText color="danger">{error}</IonText>
					<IonText color="success">{msg}</IonText>
				</IonContent>
			</IonModal>

			<IonPage>
				<IonHeader>
					<IonToolbar>
						<IonButtons slot="start">
							<IonMenuButton />
						</IonButtons>
						<IonButtons slot="end">
							<IonButton
								id="open-modal"
								onClick={() => {
									setIsModalOpen(true);
									clearForm();
								}}
							>
								<PlusIcon />
							</IonButton>
						</IonButtons>
						<IonTitle>{t("rents")}</IonTitle>
					</IonToolbar>
				</IonHeader>

				<IonContent fullscreen>
					<IonHeader collapse="condense">
						<IonToolbar>
							<IonTitle size="large">{t("rents")}</IonTitle>
						</IonToolbar>
					</IonHeader>
					<IonGrid>
						<IonRow>
							<IonCol size="6">
								<Date
									id="from"
									label={t("from")}
									value={from}
									onChange={(e: any) =>
										setFrom(e.target.value)
									}
								/>
							</IonCol>
							<IonCol size="6">
								<Date
									id="to"
									label={t("to")}
									value={to}
									onChange={(e: any) => setTo(e.target.value)}
								/>
							</IonCol>
							<IonCol size="12">
								<IonCard>
									{data.length === 0 && (
										<IonCardContent>
											<NoDataFound />
										</IonCardContent>
									)}
									{data.length > 0 && (
										<IonCardContent
											style={{
												width: "100%",
												overflowY: "auto",
												overflowX: "auto"
											}}
										>
											{size.screen === BreakPoint.xs ||
											size.screen === BreakPoint.s ? (
												<IonList>
													{data.map(elem => (
														<IonItem
															key={elem._id}
															className="zero-padding-item"
														>
															<IonLabel>
																{
																	elem.description
																}{" "}
																<br />
																<IonNote
																	style={{
																		fontSize: 12
																	}}
																>
																	{moment(
																		elem.date
																	).format(
																		"DD-MMM-YYYY"
																	)}
																</IonNote>
															</IonLabel>
															<IonLabel>
																{t(
																	"currencySymbol"
																)}
																{Utils.formatToCurrency(
																	elem.amount
																)}
																<br />
																<IonNote
																	style={{
																		fontSize: 12
																	}}
																>
																	{
																		elem
																			.propertyId
																			?.address
																	}
																</IonNote>
															</IonLabel>
															<IonButtons
																slot="end"
																style={{
																	marginLeft: 0,
																	marginRight: 0
																}}
															>
																<IonButton
																	fill="clear"
																	id="open-modal"
																	onClick={() =>
																		handleEdit(
																			elem?._id
																		)
																	}
																>
																	<IonIcon
																		icon={
																			pencil
																		}
																		slot="icon-only"
																	/>
																</IonButton>
																<IonButton
																	fill="clear"
																	color="danger"
																	onClick={() =>
																		handleDelete(
																			elem?._id
																		)
																	}
																>
																	<IonIcon
																		icon={
																			trash
																		}
																		slot="icon-only"
																	/>
																</IonButton>
															</IonButtons>
														</IonItem>
													))}
												</IonList>
											) : (
												<table
													style={{
														width: "100%",
														overflowX: "auto"
													}}
												>
													<tbody>
														{data.map(elem => (
															<tr key={elem._id}>
																<td>
																	{
																		elem.description
																	}
																</td>
																<td>
																	{moment(
																		elem.date
																	).format(
																		"DD-MMM-YYYY"
																	)}
																</td>
																<td>
																	{
																		elem
																			.propertyId
																			?.address
																	}
																</td>
																<td>
																	{
																		elem
																			.tenantId
																			?.name
																	}
																</td>
																<td className="ion-text-end">
																	{t(
																		"currencySymbol"
																	)}
																	{Utils.formatToCurrency(
																		elem.amount
																	)}
																</td>

																<td className="ion-text-end">
																	<IonButton
																		fill="clear"
																		color="dark"
																		id="open-modal"
																		onClick={() =>
																			handleEdit(
																				elem?._id
																			)
																		}
																	>
																		{t(
																			"edit"
																		)}
																	</IonButton>
																	<IonButton
																		fill="clear"
																		color="danger"
																		onClick={() =>
																			handleDelete(
																				elem?._id
																			)
																		}
																	>
																		{t(
																			"delete"
																		)}
																	</IonButton>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											)}
										</IonCardContent>
									)}
								</IonCard>
							</IonCol>
						</IonRow>
					</IonGrid>
				</IonContent>
			</IonPage>
		</>
	);
};

export default Expanses;
